import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, Button, Typography, CircularProgress, MenuItem, Select, InputLabel, FormControl, Card, Grid } from "@mui/material";
import { Delete, Upload } from "@mui/icons-material";
import { db, storage } from "./firebaseServices"; // Import Firestore and Firebase storage
import { getDocs, getDoc, collection, doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import SnackbarMessage from "./SnackbarMessage"; // Import Snackbar component

function AddEditImages() {
  const [tabValue, setTabValue] = useState(0); // Track active tab
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [images, setImages] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    // Fetch products when the component mounts
    const fetchProducts = async () => {
      try {
        const productSnapshot = await getDocs(collection(db, "products"));
        const productList = productSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        productList.sort((a, b) => a.sku.localeCompare(b.sku)); // Sort by SKU
        setProducts(productList);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedProduct("");
    setImages([]);
    setMessage("");
  };

  const handleProductSelect = async (e) => {
    const productId = e.target.value;
    setSelectedProduct(productId);

    if (productId) {
      try {
        const productDoc = await getDoc(doc(db, "products", productId)); // Use getDoc instead of getDocs
        if (productDoc.exists()) {
          const productData = productDoc.data();
          setImages(productData.images || []);
        }
      } catch (error) {
        console.error("Error fetching product images:", error);
      }
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();

    if (!selectedProduct || !imageFile) {
      setMessage("Please select a product and choose an image.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const storageRef = ref(storage, `products/${selectedProduct}/${imageFile.name}`);
      await uploadBytes(storageRef, imageFile);

      const downloadURL = await getDownloadURL(storageRef);

      const productRef = doc(db, "products", selectedProduct);
      await updateDoc(productRef, {
        images: arrayUnion(downloadURL),
      });

      setImages((prevImages) => [...prevImages, downloadURL]);
      setMessage("Image uploaded successfully!");
      setImageFile(null);
    } catch (error) {
      console.error("Error uploading image:", error);
      setMessage("Failed to upload image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = async (imageUrl) => {
    if (!selectedProduct) return;

    setLoading(true);

    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);

      const productRef = doc(db, "products", selectedProduct);
      await updateDoc(productRef, {
        images: arrayRemove(imageUrl),
      });

      setImages((prevImages) => prevImages.filter((img) => img !== imageUrl));
      setMessage("Image removed successfully!");
      setImageFile(null);
    } catch (error) {
      console.error("Error removing image:", error);
      setMessage("Failed to remove image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceImage = async (e, oldImageUrl) => {
    if (!selectedProduct) {
      setMessage("Please select a product before replacing an image.");
      return;
    }

    const file = e.target.files[0]; // Get the new image file
    if (!file) return;

    setLoading(true);
    setMessage("");

    try {
      // Delete the old image from Firebase Storage
      const oldImageRef = ref(storage, oldImageUrl);
      await deleteObject(oldImageRef);

      // Upload the new image

      const newImageRef = ref(storage, `products/${selectedProduct}/${file.name}`);
      await uploadBytes(newImageRef, file);

      const newDownloadURL = await getDownloadURL(newImageRef);

      const productRef = doc(db, "products", selectedProduct);

      // First, remove the old image
      await updateDoc(productRef, {
        images: arrayRemove(oldImageUrl),
      });

      // Then, add the new image
      await updateDoc(productRef, {
        images: arrayUnion(newDownloadURL),
      });

      // Update state: Replace the old image with the new one
      setImages((prevImages) => prevImages.map((img) => (img === oldImageUrl ? newDownloadURL : img)));

      setMessage("Image replaced successfully!");
    } catch (error) {
      console.error("Error replacing image:", error);
      setMessage("Failed to replace image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Card
        sx={{
          backgroundColor: "white",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          borderRadius: "8px",
          padding: 3,
          maxWidth: "900px",
          margin: "auto",
          mt: 4,
        }}
      >
        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab label="Add Images" />
          <Tab label="Edit Images" />
        </Tabs>

        {tabValue === 0 && (
          <Box component="form" onSubmit={handleImageUpload} sx={{ mt: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Product</InputLabel>
              <Select value={selectedProduct} onChange={handleProductSelect} required>
                {products.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    <Typography sx={{ fontWeight: "bold" }}>{product.sku} </Typography> - {product.productName} {/* Display SKU and product name */}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box
              sx={{
                display: "flex", // Use Flexbox
                justifyContent: "space-between", // Space buttons on opposite sides
                alignItems: "center", // Align buttons vertically
                mt: 2, // Add top margin
              }}
            >
              <Button variant="contained" component="label" sx={{ mt: 2 }}>
                Select Image
                <input type="file" hidden accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
              </Button>
              {imageFile && <Typography>{imageFile.name}</Typography>}
              <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }} disabled={loading ? true : !selectedProduct || !imageFile}>
                {loading ? <CircularProgress size={24} /> : "Upload Image"}
              </Button>
            </Box>
            <SnackbarMessage message={message} setMessage={setMessage} /> {/* Include Snackbar */}
            <Grid container spacing={2} sx={{ mt: 3 }}>
              {images.map((image, index) => (
                <Grid item xs={4} key={index}>
                  <img
                    src={image}
                    alt={`Product  ${index}`}
                    style={{
                      width: "100%",
                      height: "auto",
                      borderRadius: "8px",
                      boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.2)",
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ mt: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Product</InputLabel>
              <Select value={selectedProduct} onChange={handleProductSelect} required>
                {products.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    <Typography sx={{ fontWeight: "bold" }}>{product.sku} </Typography> - {product.productName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              {images.map((image, index) => (
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                    borderBottom: "1px solid #e0e0e0",
                  }}
                  key={index}
                >
                  <Typography>{`Image ${index + 1}`}</Typography>
                  <img
                    src={image}
                    alt={`Product  ${index}`}
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "4px",
                      objectFit: "cover",
                    }}
                  />
                  <Box>
                    <Button variant="outlined" color="error" onClick={() => handleRemoveImage(image)} startIcon={<Delete />} sx={{ mr: 1 }}>
                      Remove
                    </Button>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<Upload />}
                      disabled={!selectedProduct} // Prevent replacing images if no product is selected
                    >
                      Replace
                      <input type="file" hidden accept="image/*" onChange={(e) => handleReplaceImage(e, image)} />
                    </Button>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <SnackbarMessage message={message} setMessage={setMessage} /> {/* Include Snackbar */}
          </Box>
        )}
      </Card>
    </Box>
  );
}

export default AddEditImages;
